import moment, { Moment } from "moment";

/**
 * Date handling for SSRS report parameters.
 *
 * Split out from the dashboard so the label heuristic below can be tested on its own -- it is the
 * part most likely to be wrong, and it is the part a rendered component test would exercise least.
 */

// SSRS hands date parameters back in whatever shape the report author chose. A parameter typed
// DateTime arrives as full ISO ("2026-05-24T10:56:10.0000000"), but plenty of reports here declare
// Start Date / End Date as String and format them themselves -- those arrive looking like
// "5/24/2026". Anything the user has already picked in this session is a bare yyyy-MM-dd.
//
// All of these have to parse, and every format is listed explicitly: moment's fallback parser
// accepts almost any string and would turn a typo into a real date rather than rejecting it.
export const DATE_FORMATS: (string | typeof moment.ISO_8601)[] = [
  moment.ISO_8601,
  "YYYY-MM-DD",
  "M/D/YYYY",
  "MM/DD/YYYY",
  "M-D-YYYY",
  "YYYY/MM/DD",
];

export const parseParamDate = (v: string): Moment | null => {
  if (!v) return null;
  const parsed = moment(v, DATE_FORMATS as any, true);
  return parsed.isValid() ? parsed : null;
};

/**
 * What shape to send back to SSRS.
 *
 * This matters because a String parameter has a contract we cannot see. If the report's own default
 * is "5/24/2026", the query behind it may well be parsing M/D/YYYY, and quietly switching to
 * yyyy-MM-dd risks a report that errors -- or worse, one that returns the wrong rows without saying
 * so. So echo back whichever format the report itself used.
 *
 * A DateTime parameter is the exception: its ISO default carries a time component that is never
 * meaningful for a date picker, and yyyy-MM-dd is what the previous native <input type="date">
 * submitted, so those keep that shape.
 */
export const outputFormatFor = (defaultValue: string | undefined): string => {
  if (!defaultValue) return "YYYY-MM-DD";
  if (moment(defaultValue, moment.ISO_8601, true).isValid()) return "YYYY-MM-DD";
  const matched = ["M/D/YYYY", "MM/DD/YYYY", "M-D-YYYY", "YYYY/MM/DD"].find((f) =>
    moment(defaultValue, f, true).isValid()
  );
  return matched ?? "YYYY-MM-DD";
};

/**
 * Does this parameter look like a date even though SSRS did not say so?
 *
 * Start Date and End Date are the most common inputs on these reports and a lot of them are declared
 * as String, so type alone misses them. Matching the label is the only signal left.
 *
 * Two things keep this from over-firing. Parameters with a value list never reach here -- they are
 * rendered as a select further up -- so this only ever applies to a free-text box, where the worst
 * case is a calendar on something that wanted typing. And the exclusions below drop the labels that
 * contain "date" while meaning something else: a DateType or DateRange selector is a choice, not a
 * day.
 */
const DATE_LABEL = /\bdate\b/i;
const NOT_A_DAY = /\b(type|kind|range|group|grouping|part|format|option|period)\b/i;

// Prompts are written for people ("Start Date"), but names are usually camelCase ("StartDate"), and
// there is no word boundary inside that for \b to match on. Split the case transitions so the two
// read alike before testing either.
const spaceOutCamelCase = (v: string): string => v.replace(/([a-z0-9])([A-Z])/g, "$1 $2");

export const looksLikeDateParam = (name: string, prompt: string | null | undefined): boolean => {
  const label = spaceOutCamelCase(`${prompt ?? ""} ${name ?? ""}`);
  return DATE_LABEL.test(label) && !NOT_A_DAY.test(label);
};
